import {isValid} from 'date-fns';
import {Button, Flag, Tabs, type TabProps} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {useState} from 'react';

import {isNil, isNotNil} from 'ramda';

import type {AbcXyzAnalysisDetailResponse} from '@dms/api/reporting';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';

import {
  composePath,
  EMPTY_PLACEHOLDER,
  parseDate,
  type RequiredTestIdProps,
  suffixTestId,
} from 'shared/web';

import {AbcXyzAbcTab} from './AbcXyzAbcTab';
import {AbcXyzOverviewTab} from './AbcXyzOverviewTab';
import {AbcXyzXyzTab} from './AbcXyzXyzTab';

const OVERVIEW_TAB = 'overview';
const ABC_TAB = 'abc';
const XYZ_TAB = 'xyz';

const CONTEXT_SEPARATOR = ' · ';

interface AbcXyzRowDetailContentProps extends RequiredTestIdProps {
  detail: AbcXyzAnalysisDetailResponse;
  /** Article of the row, which every grid of the dialog is filtered by. Not shown to the user. */
  articleId: string;
}

export function AbcXyzRowDetailContent(props: AbcXyzRowDetailContentProps) {
  const [activeTab, setActiveTab] = useState(OVERVIEW_TAB);
  const formatDateTime = useDateTimeFormatter();

  const snapshotDate = parseDate(props.detail.abcSnapshotDate ?? props.detail.xyzSnapshotDate);
  const snapshot =
    isNotNil(snapshotDate) && isValid(snapshotDate)
      ? formatDateTime('dateShort', snapshotDate)
      : EMPTY_PLACEHOLDER;

  const contextItems = [
    `${i18n.t('entity.warehouse.labels.warehouse')}: ${props.detail.warehouseName ?? EMPTY_PLACEHOLDER}`,
    `${i18n.t('entity.warehouse.labels.branch')}: ${props.detail.branchName ?? EMPTY_PLACEHOLDER}`,
    `${i18n.t('entity.warehouse.reporting.snapshotDate')}: ${snapshot}`,
  ];

  const stockCardPath = isNil(props.detail.warehouseId)
    ? null
    : composePath(warehouseRoutes.articleDetailOverview, {
        params: {id: props.articleId, warehouseId: props.detail.warehouseId},
      });

  const tabs: TabProps[] = [
    {
      id: OVERVIEW_TAB,
      title: i18n.t('general.labels.overview'),
      content: (
        <AbcXyzOverviewTab
          detail={props.detail}
          articleId={props.articleId}
          data-testid={suffixTestId('overview', props)}
        />
      ),
    },
    {
      id: ABC_TAB,
      title: i18n.t('entity.warehouse.reporting.abcAnalysis'),
      content: (
        <AbcXyzAbcTab
          detail={props.detail}
          articleId={props.articleId}
          data-testid={suffixTestId('abc', props)}
        />
      ),
    },
    {
      id: XYZ_TAB,
      title: i18n.t('entity.warehouse.reporting.xyzAnalysis'),
      content: (
        <AbcXyzXyzTab
          detail={props.detail}
          articleId={props.articleId}
          data-testid={suffixTestId('xyz', props)}
        />
      ),
    },
  ];

  return (
    <VStack spacing={2} height="100%" minHeight={0}>
      <HStack spacing={3} align="center" justify="space-between" shrink={0} wrap>
        <HStack spacing={3} align="center" wrap>
          <HStack spacing={2} align="center" wrap>
            <Text size="large" alternative data-testid={suffixTestId('articleNumber', props)}>
              {props.detail.articleNumber ?? EMPTY_PLACEHOLDER}
            </Text>
            <Text size="large" data-testid={suffixTestId('articleName', props)}>
              {props.detail.articleName ?? EMPTY_PLACEHOLDER}
            </Text>
          </HStack>
          <Text size="small" color="secondary" data-testid={suffixTestId('context', props)}>
            {contextItems.join(CONTEXT_SEPARATOR)}
          </Text>
        </HStack>
        <HStack spacing={2} align="center">
          <Show when={props.detail.abcClass}>
            {(abcClass) => (
              <Flag
                colorScheme="green"
                label={abcClass}
                data-testid={suffixTestId('abcClass', props)}
              />
            )}
          </Show>
          <Show when={props.detail.xyzClass}>
            {(xyzClass) => (
              <Flag
                colorScheme="purple"
                label={xyzClass}
                data-testid={suffixTestId('xyzClass', props)}
              />
            )}
          </Show>
          <Show when={props.detail.abcXyzClass}>
            {(abcXyzClass) => (
              <Flag
                colorScheme="blue"
                label={abcXyzClass}
                data-testid={suffixTestId('abcXyzClass', props)}
              />
            )}
          </Show>
          <Show when={stockCardPath}>
            {(path) => (
              <Button
                variant="link"
                size="small"
                title={i18n.t('entity.warehouse.reporting.openStockCard')}
                rightIcon="action/launch"
                onClick={() => window.open(path, '_blank', 'noopener')}
                data-testid={suffixTestId('stockCardLink', props)}
              />
            )}
          </Show>
        </HStack>
      </HStack>
      <Box flex={1} minHeight={0}>
        <Tabs
          tabs={tabs}
          isFullHeight
          isLazy
          lazyBehavior="keepMounted"
          activeTabId={activeTab}
          onChange={setActiveTab}
          data-testid={suffixTestId('tabs', props)}
        />
      </Box>
    </VStack>
  );
}
